import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, shareReplay } from 'rxjs/operators';
import moment from 'moment';

import spaceTypes, { getSpaceTypeFromUrl, SpaceType } from 'src/app/shared/constants/spaceTypes.constant';

import RoomSearchParameters from 'src/app/shared/models/roomSearchParams.model';
import {
  DEFAULT_URL_COUNTRY_CODE,
  DEFAULT_URL_COUNTRY_NAME,
  DEFAULT_URL_LOCALE
} from 'src/app/shared/constants/defaultUrlParams.enum';

import { StorageService } from 'src/app/services/storage/storage.service';
import { RoomService } from 'src/app/services/room/room.service';
import { getLocationsSearchString } from 'src/app/helpers/utils/url.utils';
import { getNextDate } from 'src/app/helpers/utils/date.utils';
import { TIME_LIMITS } from 'src/app/shared/constants/timeSelection';

const TIME_FORMATS = ['H:mm', 'HH:mm'];

@Injectable({
  providedIn: 'root'
})
export class UrlParametersService {
  public spaceType = '';
  public queryParams: RoomSearchParameters;

  private paramsSubject: BehaviorSubject<RoomSearchParameters> = new BehaviorSubject<RoomSearchParameters>(null);

  public params$: Observable<RoomSearchParameters> = this.paramsSubject
    .asObservable()
    .pipe(distinctUntilChanged(), shareReplay(1));

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private storageService: StorageService,
    private roomService: RoomService
  ) {}

  public get currentParams(): RoomSearchParameters {
    return this.paramsSubject.value;
  }

  public setSpaceType(spaceType: string): void {
    this.spaceType = spaceType;
  }

  public setQueryParams(queryParams: RoomSearchParameters): void {
    this.queryParams = queryParams;
  }

  public navigate(
    updatedParams: RoomSearchParameters = {},
    commands: string[] = [],
    spaceType = '',
    clearParams = false
  ): Promise<boolean> {
    if (spaceType !== '') {
      this.setSpaceType(spaceType);
    } else if (commands.includes('manage-booking')) {
      this.setSpaceType('');
    }
    this.queryParams = Object.assign({}, clearParams ? {} : this.queryParams, updatedParams);
    const spaceTypePart = spaceTypes[this.spaceType] !== undefined ? [spaceTypes[this.spaceType].path] : [];
    const currentFragment = this.router.parseUrl(this.router.url).fragment;
    return this.router.navigate([...spaceTypePart, ...commands], {
      queryParams: this.queryParams,
      relativeTo: this.route,
      queryParamsHandling: 'merge',
      fragment: currentFragment
    });
  }

  public initParams(): void {
    const defaultParams = this.getDefaultParams();
    const savedParams = this.getSavedParams() || defaultParams;
    const normalizedParams = this.normalizeParams(savedParams);

    this.setQueryParams(normalizedParams);
    this.paramsSubject.next(normalizedParams);
    this.roomService.setSearchState(normalizedParams);
  }

  private getDefaultParams(): RoomSearchParameters {
    return {
      locale: DEFAULT_URL_LOCALE,
      countryCode: DEFAULT_URL_COUNTRY_CODE,
      countryName: DEFAULT_URL_COUNTRY_NAME,
      numberOfPeople: 1
    };
  }

  private getSavedParams(): RoomSearchParameters | null {
    return (
      this.roomService.getSearchState() ||
      Object.fromEntries(new URLSearchParams(getLocationsSearchString()).entries()) ||
      null
    );
  }

  private normalizeParams(params: RoomSearchParameters): RoomSearchParameters {
    const queryToUpdate: RoomSearchParameters = {};

    if (!/^[a-zA-Z]{2}-[a-zA-Z]{2}$/.test(params.locale)) {
      queryToUpdate.locale = this.storageService.get('locale') || DEFAULT_URL_LOCALE;
    }

    if (!/^[a-zA-Z]{2}$/.test(params.countryCode)) {
      queryToUpdate.countryCode = this.storageService.get('countryCode') || DEFAULT_URL_COUNTRY_CODE;
      queryToUpdate.countryName = this.storageService.get('countryName') || DEFAULT_URL_COUNTRY_NAME;
    }

    if (params.location === '{2}') {
      queryToUpdate.location = '';
    }

    if (params.centreNumber && !/^[0-9]/.test(params.centreNumber)) {
      queryToUpdate.centreNumber = params.centreNumber;
    }

    if (!params.date) {
      queryToUpdate.date = getNextDate();
    }

    if (!params.timeFrom) {
      queryToUpdate.timeFrom = '09:00';
    }
    if (!params.timeUntil) {
      queryToUpdate.timeUntil = '17:00';
    }

    Object.assign(
      queryToUpdate,
      this.normalizeTimeRange(queryToUpdate.timeFrom ?? params.timeFrom, queryToUpdate.timeUntil ?? params.timeUntil)
    );

    if (!params.numberOfPeople) {
      queryToUpdate.numberOfPeople = this.getDefaultNumberOfPeople(this.spaceType);
    }

    // Ensure spaceType is set and default numberOfPeople is based on spaceType
    if (!this.spaceType) {
      if (params.spaceType) {
        this.setSpaceType(params.spaceType);
      } else {
        const spaceTypeFromUrl = this.getSpaceTypeFromUrl();
        this.setSpaceType(spaceTypeFromUrl);
      }
    }

    this.removeRedundantParams(params);

    return { ...params, ...queryToUpdate };
  }

  // timeUntil must be at least an hour after timeFrom (e.g. a bookmarked URL can carry an inverted range).
  private normalizeTimeRange(timeFrom: string, timeUntil: string): Pick<RoomSearchParameters, 'timeFrom' | 'timeUntil'> {
    const from = moment(timeFrom, TIME_FORMATS, true);
    const until = moment(timeUntil, TIME_FORMATS, true);
    const minUntil = from.clone().add(1, 'hours');

    if (from.isValid() && until.isValid() && !until.isBefore(minUntil)) {
      return {};
    }

    const maxUntil = moment(TIME_LIMITS.timeUntil.end.string, TIME_FORMATS, true);
    if (from.isValid() && !minUntil.isAfter(maxUntil)) {
      return { timeUntil: minUntil.format('HH:mm') };
    }

    return { timeFrom: '09:00', timeUntil: '17:00' };
  }

  private getDefaultNumberOfPeople(spaceType: string): number {
    return spaceType === SpaceType.MEETING_ROOM ? 2 : 1;
  }

  private getSpaceTypeFromUrl(): string | undefined {
    const urlPart = window.location.hash.substring(1).split('?')[0];
    return getSpaceTypeFromUrl(urlPart);
  }

  private removeRedundantParams(params: Partial<RoomSearchParameters>): void {
    params.paginationSize = null;
    params.searchRadiusInMilesTo = null;
    params.searchRangeInMilesTo = null;
  }
}
